
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-gradient-to-r from-[#1a0e05]/95 via-[#2a1508]/95 to-[#1a0e05]/95 backdrop-blur-md shadow-lg shadow-amber-900/10 border-b border-amber-700/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
              isScrolled
                ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-md shadow-amber-500/30'
                : 'bg-white/15 backdrop-blur-sm border border-white/25'
            }`}>
              <i className="ri-ship-line text-white text-2xl"></i>
            </div>
            <div>
              <h1 className={`text-xl font-bold transition-colors duration-500 ${
                isScrolled ? 'text-amber-50' : 'text-white drop-shadow-md'
              }`}>만선허브</h1>
              <p className={`text-xs transition-colors duration-500 ${
                isScrolled ? 'text-amber-200/70' : 'text-white/70'
              }`}>어선 물품 공급 플랫폼</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {[
              { to: '/', label: '홈' },
              { to: '/products', label: '제품 카탈로그' },
              { to: '/manufacturers', label: '제조업체' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative font-medium whitespace-nowrap transition-colors duration-300 group ${
                  isScrolled
                    ? 'text-amber-100/80 hover:text-amber-300'
                    : 'text-white/85 hover:text-white'
                }`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 rounded-full transition-all duration-300 group-hover:w-full ${
                  isScrolled ? 'bg-amber-400' : 'bg-amber-300'
                }`}></span>
              </Link>
            ))}
            <a
              href="#about"
              className={`relative font-medium whitespace-nowrap transition-colors duration-300 group ${
                isScrolled
                  ? 'text-amber-100/80 hover:text-amber-300'
                  : 'text-white/85 hover:text-white'
              }`}
            >
              회사소개
              <span className={`absolute -bottom-1 left-0 w-0 h-0.5 rounded-full transition-all duration-300 group-hover:w-full ${
                isScrolled ? 'bg-amber-400' : 'bg-amber-300'
              }`}></span>
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <button className={`px-5 py-2.5 font-medium whitespace-nowrap transition-all duration-300 rounded-lg cursor-pointer ${
              isScrolled
                ? 'text-amber-200 hover:text-amber-100 hover:bg-white/5'
                : 'text-white/90 hover:text-white hover:bg-white/10'
            }`}>
              로그인
            </button>
            <button className={`px-5 py-2.5 font-medium whitespace-nowrap rounded-lg transition-all duration-300 cursor-pointer ${
              isScrolled
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 shadow-md shadow-amber-600/30'
                : 'bg-amber-500/90 text-white hover:bg-amber-500 shadow-lg shadow-amber-500/20'
            }`}>
              무료 시작하기
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 transition-colors cursor-pointer ${
              isScrolled ? 'text-amber-100 hover:text-amber-300' : 'text-white hover:text-amber-300'
            }`}
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className={`md:hidden py-4 border-t ${
            isScrolled ? 'border-amber-700/30' : 'border-white/20'
          }`}>
            <div className="flex flex-col gap-4">
              {[
                { to: '/', label: '홈' },
                { to: '/products', label: '제품 카탈로그' },
                { to: '/manufacturers', label: '제조업체' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium whitespace-nowrap transition-colors ${
                    isScrolled
                      ? 'text-amber-100/80 hover:text-amber-300'
                      : 'text-white/85 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="#about"
                onClick={() => setIsMenuOpen(false)}
                className={`font-medium whitespace-nowrap transition-colors ${
                  isScrolled
                    ? 'text-amber-100/80 hover:text-amber-300'
                    : 'text-white/85 hover:text-white'
                }`}
              >
                회사소개
              </a>
              <div className={`flex flex-col gap-3 pt-4 border-t ${
                isScrolled ? 'border-amber-700/30' : 'border-white/20'
              }`}>
                <button className={`px-5 py-2.5 font-medium text-left whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-amber-200' : 'text-white/90'
                }`}>
                  로그인
                </button>
                <button className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium whitespace-nowrap cursor-pointer">
                  무료 시작하기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
